// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md #1
// Cliente detail — wired real component matching mockup design.
// Sections: Hero header · Anchor nav · Match · Contatto · Sopralluoghi · Preferenze · Partecipazioni · Anagrafica · Note · Documenti · Score rail.
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import { patchClient, patchClientStage } from '../lib/api'
import { clientStageInfo } from '../lib/clientStage'
import { useClient } from '../hooks/useClient'
import { useMe } from '../hooks/useAuth'
import { useActivity } from '../hooks/useActivity'
import { useProposalsList } from '../hooks/useProposals'
import { useUsersList } from '../hooks/useComments'
import { canWriteClient, isFullView } from '../lib/permissions'
import { CommentsButton } from '../components/CommentsButton'
import { SendMessageModal } from '../components/SendMessageModal'
import { NewChatModal } from '../components/NewChatModal'
import { HeaderMenu } from '../components/HeaderMenu'
import { ClienteAnagrafica } from '../components/cliente/ClienteAnagrafica'
import { ClientePreferenze } from '../components/cliente/ClientePreferenze'
import { ClienteNote } from '../components/cliente/ClienteNote'
import { ClienteDocumenti } from '../components/cliente/ClienteDocumenti'
import { ClienteAnagraficaModal } from '../components/cliente/ClienteAnagraficaModal'
import { ClientePreferenzeModal } from '../components/cliente/ClientePreferenzeModal'
import { ClienteNoteModal } from '../components/cliente/ClienteNoteModal'
import { ClienteScoreRail } from '../components/cliente/ClienteScoreRail'
import { ClienteCompletezzaCard } from '../components/cliente/ClienteCompletezzaCard'
import { ClienteMatchHero } from '../components/cliente/ClienteMatchHero'
import { ClienteSopralluoghiInline } from '../components/cliente/ClienteSopralluoghiInline'
import { ActivityTimeline } from '../components/ActivityTimeline'
import { CardSkeleton } from '../components/Skeleton'
import { ScdRequestsList } from '../components/scd/ScdRequestsList'

export const Route = createFileRoute('/clienti/$id')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  component: ClienteDetailPage,
})

function shortCode(code: number | null | undefined): string {
  if (code === null || code === undefined) return ''
  return `C_${code.toString().padStart(5, '0')}`
}


type ActivityLite = {
  id: string
  kind: string
  title: string
  body?: string | null
  occurred_at: string
  payload?: Record<string, unknown> | null
}

function NumerikChiaveCard({ items }: { items: ActivityLite[] }) {
  const lottiVisti = items.filter((a) => a.kind === 'match').length
  const sopralluoghi = items.filter((a) => a.kind === 'sopralluogo').length
  const partecipazioni = items.filter((a) => a.kind === 'partecipazione').length
  const aggiudic = items.filter(
    (a) =>
      a.kind === 'partecipazione' &&
      typeof a.payload?.esito === 'string' &&
      a.payload.esito.includes('aggiud'),
  ).length
  const spesoSum = items.reduce((acc, a) => {
    const v = a.payload?.prezzo
    return typeof v === 'number' ? acc + v : acc
  }, 0)
  const convers =
    sopralluoghi > 0
      ? Math.round((partecipazioni / sopralluoghi) * 100)
      : 0

  return (
    <aside className="section" aria-label="Numeri chiave cliente">
      <div className="section-head">
        <div className="title">Numeri chiave</div>
      </div>
      <div
        className="section-body"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'var(--sp-2)',
          padding: 'var(--sp-3)',
          textAlign: 'center',
        }}
      >
        <Cell value={lottiVisti} label="Lotti visti" />
        <Cell value={sopralluoghi} label="Sopralluoghi" />
        <Cell value={partecipazioni} label="Partecipaz." />
        <Cell value={aggiudic} label="Aggiudic." />
        <Cell
          value={
            spesoSum > 0
              ? `€ ${Math.round(spesoSum / 1000)}k`
              : '€ 0'
          }
          label="Speso"
        />
        <Cell value={`${convers}%`} label="Convers." accent />
      </div>
    </aside>
  )
}

function Cell({
  value,
  label,
  accent,
}: {
  value: number | string
  label: string
  accent?: boolean
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--fs-16)',
          fontWeight: 'var(--fw-semibold)',
          fontVariantNumeric: 'tabular-nums',
          color: accent ? 'var(--warn-ink)' : 'var(--ink-1)',
        }}
      >
        {value}
      </div>
      <div
        className="muted"
        style={{
          fontSize: 'var(--fs-10)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
        }}
      >
        {label}
      </div>
    </div>
  )
}

function TeamAssegnatoCard({
  consultantName,
  clientId,
  consultantId,
  canAssign,
}: {
  consultantName?: string | null
  clientId: string
  consultantId?: string | null
  canAssign: boolean
}) {
  const queryClient = useQueryClient()
  const usersQuery = useUsersList()
  const assign = useMutation({
    mutationFn: (newConsultantId: string) =>
      patchClient(clientId, { consultant_id: newConsultantId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })

  const consultants = (usersQuery.data?.items ?? []).filter(
    (u) => u.role === 'consulente',
  )

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        flexWrap: 'wrap',
        marginTop: 'var(--sp-2)',
      }}
    >
      <span
        className="muted"
        style={{
          fontSize: 'var(--fs-11)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          fontWeight: 'var(--fw-medium)',
        }}
      >
        Responsabile
      </span>
      <span style={{ fontSize: 'var(--fs-13)', fontWeight: 'var(--fw-semibold)' }}>
        {consultantName ?? 'Non assegnato'}
      </span>
      {canAssign && (
        <select
          value={consultantId ?? ''}
          disabled={assign.isPending}
          onChange={(e) => {
            if (e.target.value) assign.mutate(e.target.value)
          }}
          aria-label="Assegna o riassegna responsabile"
          style={{ fontSize: 'var(--fs-12)' }}
        >
          <option value="">
            {consultantName ? 'Riassegna…' : 'Assegna…'}
          </option>
          {consultants.map((u) => (
            <option key={u.id} value={u.id}>
              {u.display_name}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

function ClienteDetailPage() {
  const matchRoute = useMatchRoute()
  const onChild = matchRoute({ to: '/clienti/$id/match' })
  if (onChild) return <Outlet />
  return <ClienteDetail />
}

function ClienteDetail() {
  const { id } = Route.useParams()
  const { data: me } = useMe()
  const { data, isLoading, isError } = useClient(id)
  const activityQuery = useActivity({ client_id: id, page_size: 200 })
  const navigate = useNavigate()
  // Backlog Proposal pending per questo cliente — alimenta icona "lotti da
  // segnalare" nel hero + badge sulla tab Matching lotti.
  const backlogQ = useProposalsList({
    client_id: id,
    status: 'draft',
    auto_created: true,
    page_size: 1,
  })
  const backlogCount = backlogQ.data?.total ?? 0

  const [editAnagraficaOpen, setEditAnagraficaOpen] = useState(false)
  const [editPreferenzeOpen, setEditPreferenzeOpen] = useState(false)
  const [editNoteOpen, setEditNoteOpen] = useState(false)
  const [messageOpen, setMessageOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [uploadDocsNonce, setUploadDocsNonce] = useState(0)
  const canEdit = canWriteClient(me)

  const queryClient = useQueryClient()
  const patchStage = useMutation({
    mutationFn: (stage: string) => patchClientStage(data!.id, stage),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', id] })
    },
  })

  const code = data ? shortCode(data.short_code) : ''
  const cognome = data?.cognome ?? ''
  const nome = data?.nome ?? ''
  const fullName = `${cognome} ${nome}`.trim()
  const displayTitle = fullName || 'Cliente'

  // Activity counters per anchor nav.
  const activityItems = activityQuery.data?.items ?? []
  const cnt = (kind: string) =>
    activityItems.filter((a) => a.kind === kind).length
  const ctSopralluoghi = cnt('sopralluogo')
  const ctPartecipazioni = cnt('partecipazione')
  const ctStorico = activityItems.length
  const ctDocs = cnt('allegato_upload')

  const stageInfo = clientStageInfo(data?.client_stage)

  return (
    <>
      {/* CRUMB row */}
      <div className="crumb-row">
        <div className="breadcrumb">
          <span>Operativo</span>
          <span className="sep">/</span>
          <Link
            to="/clienti"
            search={{ page: 1, page_size: 50, q: '', sort: '-created_at' }}
          >
            Clienti
          </Link>
          <span className="sep">/</span>
          <span className="here">
            {code} · {displayTitle}
          </span>
        </div>
      </div>

      {isLoading && <CardSkeleton />}
      {isError && (
        <div
          className="muted"
          style={{ padding: 'var(--sp-4)', fontStyle: 'italic' }}
        >
          Cliente non trovato o errore di caricamento.
        </div>
      )}

      {data && (
        <>
          {/* HERO */}
          <div className="hero detail-hero">
            <div className="hero-main">
              {/* ROW 1: title + code chip + stage menu */}
              <div className="hero-titlerow">
                <h1 className="cli-title">{displayTitle}</h1>
                <span className="cli-code">
                  {code}
                  <button
                    type="button"
                    className="icon-copy"
                    title="Copia codice"
                    onClick={() => {
                      void navigator.clipboard?.writeText(code)
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      marginLeft: 6,
                    }}
                  >
                    📋
                  </button>
                </span>
                <div
                  className="hero-stage-block"
                  style={{ marginLeft: 'var(--sp-3)' }}
                >
                  {canEdit ? (
                    <HeaderMenu
                      ariaLabel="Cambia stage cliente"
                      align="left"
                      items={[
                        {
                          key: 'in_lavorazione',
                          label: 'In lavorazione',
                          current: data.client_stage === 'in_lavorazione',
                          disabled: patchStage.isPending,
                          onClick: () => patchStage.mutate('in_lavorazione'),
                        },
                        {
                          key: 'sospeso',
                          label: 'Sospeso',
                          current: data.client_stage === 'sospeso',
                          disabled: patchStage.isPending,
                          onClick: () => patchStage.mutate('sospeso'),
                        },
                        {
                          key: 'perso',
                          label: 'Perso',
                          danger: true,
                          current: data.client_stage === 'perso',
                          disabled: patchStage.isPending,
                          onClick: () => {
                            if (window.confirm('Segnare il cliente come perso?')) {
                              patchStage.mutate('perso')
                            }
                          },
                        },
                      ]}
                    >
                      <span
                        className={`cli-stage ${stageInfo.cls}`}
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        title="Cambia stage"
                      >
                        {stageInfo.label}
                        <span aria-hidden>▾</span>
                      </span>
                    </HeaderMenu>
                  ) : (
                    <span className={`cli-stage ${stageInfo.cls}`}>{stageInfo.label}</span>
                  )}
                </div>
                {/* Primary actions inline with row 1 (pushed right) */}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn info"
                    onClick={() =>
                      void navigate({
                        to: '/clienti/$id/match',
                        params: { id },
                        search: {} as never,
                      })
                    }
                  >
                    🎯 Match lotti
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setEditAnagraficaOpen(true)}
                      title="Modifica anagrafica completa"
                    >
                      ✎ Modifica
                    </button>
                  )}
                  {canEdit && (
                    <HeaderMenu
                      ariaLabel="Altre azioni"
                      align="right"
                      buttonClassName="btn sm ghost"
                      items={[
                        {
                          key: 'template',
                          label: 'Template MSG',
                          icon: '📋',
                          onClick: () => setMessageOpen(true),
                        },
                        {
                          key: 'chat',
                          label: 'Chat team',
                          icon: '🗨️',
                          onClick: () => setChatOpen(true),
                        },
                        {
                          key: 'upload',
                          label: 'Carica documenti',
                          icon: '📤',
                          onClick: () => setUploadDocsNonce((n) => n + 1),
                        },
                      ]}
                    >
                      ⋯ Altro
                    </HeaderMenu>
                  )}
                  <CommentsButton entityType="client" entityId={id} title={code} />
                </div>
              </div>

              {/* ROW 2: contacts + cerca summary + responsabile + status chips */}
              <div
                className="hero-meta"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--sp-2)',
                  alignItems: 'center',
                  fontSize: 'var(--fs-13)',
                  color: 'var(--muted)',
                  marginTop: 'var(--sp-2)',
                }}
              >
                {/* Compact contacts */}
                {data.telefono && (
                  <a
                    href={`tel:${data.telefono.replace(/\s/g, '')}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
                  >
                    📞 <span className="mono">{data.telefono}</span>
                  </a>
                )}
                {data.whatsapp_attivo && data.telefono && (
                  <a
                    className="wa"
                    href={`https://wa.me/${data.telefono.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
                  >
                    💬 WA
                  </a>
                )}
                {data.email && (
                  <a
                    href={`mailto:${data.email}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
                  >
                    ✉ <span className="mono">{data.email}</span>
                  </a>
                )}

                {/* Responsabile — in alto a destra, sulla riga dei contatti */}
                <div style={{ marginLeft: 'auto' }}>
                  <TeamAssegnatoCard
                    consultantName={data.consultant_name}
                    clientId={id}
                    consultantId={data.consultant_id}
                    canAssign={isFullView(me)}
                  />
                </div>

                {/* Line-break: Cerca sotto i contatti */}
                <div style={{ flexBasis: '100%', height: 0 }} aria-hidden />

                {/* Backlog "da segnalare" — inizio riga Cerca */}
                {backlogCount > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      void navigate({
                        to: '/clienti/$id/match',
                        params: { id },
                        search: {} as never,
                      })
                    }
                    title={`${backlogCount} lott${backlogCount === 1 ? 'o' : 'i'} da segnalare al cliente — apri tab Da segnalare`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px',
                      background: 'oklch(96% 0.05 80)',
                      color: 'oklch(45% 0.15 60)',
                      border: '1px solid oklch(85% 0.08 80)',
                      borderRadius: 'var(--r-pill)',
                      fontSize: 'var(--fs-12)',
                      fontWeight: 'var(--fw-semibold)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    🔔 {backlogCount} da segnalare
                  </button>
                )}

                {/* Cosa cerca summary */}
                {(data.pref_budget_max ||
                  (data.zone_tag && data.zone_tag.length > 0)) && (
                  <span style={{ color: 'var(--muted)' }}>
                    🔎 Cerca:{' '}
                    {data.pref_budget_max
                      ? `€${Math.round(data.pref_budget_max / 1000)}k`
                      : ''}
                    {data.pref_budget_max &&
                    data.zone_tag &&
                    data.zone_tag.length > 0
                      ? ' · '
                      : ''}
                    {data.zone_tag && data.zone_tag.length > 0
                      ? data.zone_tag.slice(0, 2).join(', ')
                      : ''}
                  </span>
                )}

                {/* Separatore: chip sulla stessa riga di da-segnalare/Cerca (come mockup).
                   Solo se c'è qualcosa prima (backlog o Cerca), altrimenti niente "|" orfano. */}
                {(backlogCount > 0 ||
                  data.pref_budget_max ||
                  (data.zone_tag?.length ?? 0) > 0) && (
                  <span
                    aria-hidden
                    style={{
                      width: 1,
                      height: 16,
                      background: 'var(--line)',
                      margin: '0 4px',
                    }}
                  />
                )}

                {/* Status / rating / tipologia chips */}
                {data.priorita && (
                  <span className={`prio ${data.priorita.toLowerCase()}`}>
                    <span className="dot"></span>
                    {data.priorita}
                  </span>
                )}
                {data.rating && (
                  <span className={`rating-lg ${data.rating.toLowerCase()}`}>
                    {data.rating}
                  </span>
                )}
                {data.tipologia && (
                  <span
                    className={`tag tipo-${data.tipologia.toLowerCase().replace(/_/g, '-')}`}
                  >
                    {data.tipologia.replace(/_/g, ' ')}
                  </span>
                )}
                {/* Mutuo / affordability chip */}
                {data.modalita_acquisto === 'LIQUIDITA' && (
                  <span
                    className="tag"
                    style={{
                      background: 'oklch(96% 0.04 150)',
                      color: 'oklch(45% 0.12 150)',
                      border: '1px solid oklch(85% 0.06 150)',
                    }}
                  >
                    💧 Liquidità
                  </span>
                )}
                {data.modalita_acquisto === 'MUTUO_LIQUIDITA' &&
                  data.mutuo_pre_approvato && (
                    <span
                      className="tag"
                      style={{
                        background: 'oklch(96% 0.04 150)',
                        color: 'oklch(45% 0.12 150)',
                        border: '1px solid oklch(85% 0.06 150)',
                      }}
                    >
                      🏦 Mutuo pre-approvato
                      {data.mutuo_banca ? ` · ${data.mutuo_banca}` : ''}
                    </span>
                  )}
                {data.modalita_acquisto === 'MUTUO_LIQUIDITA' &&
                  !data.mutuo_pre_approvato && (
                    <span
                      className="tag"
                      style={{
                        background: 'oklch(96% 0.05 80)',
                        color: 'oklch(45% 0.15 60)',
                        border: '1px solid oklch(85% 0.08 80)',
                      }}
                    >
                      🏦 Mutuo da approvare
                    </span>
                  )}
              </div>
            </div>
          </div>

          {/* ANCHOR NAV — tutte le voci sono ancore in-page */}
          <nav className="anchor-nav">
            <a href="#match">
              🎯 Lotti
              {backlogCount > 0 ? (
                <span
                  className="ct"
                  style={{
                    background: 'oklch(96% 0.05 80)',
                    color: 'oklch(45% 0.15 60)',
                    border: '1px solid oklch(85% 0.08 80)',
                    fontWeight: 'var(--fw-semibold)',
                  }}
                  title="Lotti da segnalare"
                >
                  🔔 {backlogCount}
                </span>
              ) : null}
            </a>
            <a href="#sopralluoghi">
              Sopralluoghi
              {ctSopralluoghi > 0 && <span className="ct">{ctSopralluoghi}</span>}
            </a>
            <a href="#scd-requests">SCD</a>
            <a href="#preferenze">Preferenze</a>
            <a href="#partecipazioni">
              Partecipazioni
              {ctPartecipazioni > 0 && (
                <span className="ct">{ctPartecipazioni}</span>
              )}
            </a>
            <a href="#anagrafica">Anagrafica</a>
            <a href="#note">Note</a>
            <a href="#documenti">
              Documenti
              {ctDocs > 0 && <span className="ct">{ctDocs}</span>}
            </a>
            <a href="#contatto">
              Storico
              {ctStorico > 0 && <span className="ct">{ctStorico}</span>}
            </a>
            {data.consultant_name && (
              <div className="nav-end">
                <span>
                  Assegnato a{' '}
                  <strong>{data.consultant_name}</strong>
                </span>
              </div>
            )}
          </nav>

          {/* BODY — main match-first + rail stato a destra */}
          <div
            className="detail-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 320px',
              gap: 'var(--sp-3)',
              alignItems: 'start',
            }}
          >
            <div
              className="detail-main"
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
            >
              <section id="match">
                <ClienteMatchHero clientId={id} />
              </section>

              <section id="sopralluoghi">
                <ClienteSopralluoghiInline clientId={id} />
              </section>

              <section id="scd-requests">
                <ScdRequestsList
                  clientId={id}
                  client={{ id, cognome: data.cognome, nome: data.nome, budget: data.budget, liquidita: data.liquidita ?? null, scopo_acquisto: data.scopo_acquisto ?? null }}
                  title="SCD Fattibilità"
                />
              </section>

              <section id="preferenze">
                <ClientePreferenze
                  client={data}
                  onEdit={canEdit ? () => setEditPreferenzeOpen(true) : undefined}
                />
              </section>

              <section id="partecipazioni">
                <div style={{ padding: 'var(--sp-3)' }}>
                  <button
                    type="button"
                    className="btn sm ghost"
                    onClick={() =>
                      void navigate({
                        to: '/partecipazioni',
                        search: {
                          page: 1,
                          page_size: 50,
                          q: '',
                          sort: '-created_at',
                          client_id: id,
                        } as never,
                      })
                    }
                  >
                    Vedi partecipazioni →
                  </button>
                </div>
              </section>

              <section id="anagrafica">
                <ClienteAnagrafica
                  client={data}
                  onEdit={canEdit ? () => setEditAnagraficaOpen(true) : undefined}
                />
              </section>

              <section id="note">
                <ClienteNote
                  client={data}
                  onEdit={canEdit ? () => setEditNoteOpen(true) : undefined}
                />
              </section>

              <section id="documenti">
                <ClienteDocumenti
                  clientId={id}
                  canEdit={canEdit}
                  openPickerNonce={uploadDocsNonce}
                />
              </section>

              {/* Storico interazioni — in fondo alla pagina */}
              <section id="contatto">
                <ActivityTimeline
                  params={{ client_id: id, page_size: 50 }}
                  showFilters
                />
              </section>
            </div>

            <div
              className="detail-rail"
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
            >
              <ClienteCompletezzaCard
                client={data}
                onEdit={(section) =>
                  section === 'preferenze'
                    ? setEditPreferenzeOpen(true)
                    : setEditAnagraficaOpen(true)
                }
              />
              <ClienteScoreRail clientId={id} />
              <NumerikChiaveCard items={activityItems} />
            </div>
          </div>
        </>
      )}

      {editAnagraficaOpen && data && (
        <ClienteAnagraficaModal
          client={data}
          onClose={() => setEditAnagraficaOpen(false)}
        />
      )}
      {editPreferenzeOpen && data && (
        <ClientePreferenzeModal
          client={data}
          onClose={() => setEditPreferenzeOpen(false)}
        />
      )}
      {editNoteOpen && data && (
        <ClienteNoteModal
          client={data}
          onClose={() => setEditNoteOpen(false)}
        />
      )}

      {messageOpen && data && (
        <SendMessageModal
          open={messageOpen}
          onClose={() => setMessageOpen(false)}
          context={{
            client_id: id,
            client_code: code,
            client_name: fullName,
          }}
          phone={data.telefono}
          email={data.email}
        />
      )}

      {data && (
        <NewChatModal
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          contextType="client"
          clientId={id}
          contextLabel={`${code} · ${fullName}`.trim()}
        />
      )}
    </>
  )
}
